import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Plane, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { EnrollPayButton } from "../components/EnrollPayButton";
import { PRICING_ENABLED } from "../config";
import api from "../lib/api";

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const { user, ready, login, register, loginWithGoogle, refreshUser } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState(searchParams.get("name") || "");
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [legalDocs, setLegalDocs] = useState(null);
  const [accepted, setAccepted] = useState({});
  const skipAutoRedirect = useRef(false);

  useEffect(() => {
    if (ready && user && !skipAutoRedirect.current) nav("/dashboard");
  }, [ready, user, nav]);

  useEffect(() => {
    if (!isLogin && !legalDocs) api.get("/legal/docs").then((r) => setLegalDocs(r.data)).catch(() => {});
  }, [isLogin, legalDocs]);

  const requiredDocs = legalDocs ? legalDocs.required : ["terms", "privacy", "training", "beta"];
  const allAccepted = requiredDocs.every((k) => accepted[k]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isLogin && !allAccepted) {
      setError("Please accept all acknowledgments to create your account.");
      return;
    }
    setLoading(true);
    if (!isLogin) skipAutoRedirect.current = true;
    const res = isLogin ? await login(email, password) : await register(name, email, password);
    if (res.ok && !isLogin) {
      try {
        await api.post("/legal/accept", { docs: requiredDocs });
        await refreshUser();
      } catch (_) {}
    }
    setLoading(false);
    if (res.ok) {
      toast.success(isLogin ? "Welcome back, pilot!" : "Account created. Cleared for takeoff!");
      nav(isLogin ? "/dashboard" : "/welcome");
    } else {
      skipAutoRedirect.current = false;
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1577533870320-2c31e7e41028?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxmcHYlMjByYWNpbmclMjBkcm9uZXxlbnwwfHx8fDE3ODM5NjkzNjN8MA&ixlib=rb-4.1.0&q=85"
          alt="Drone" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-background/70" />
        <div className="absolute bottom-0 p-12">
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-primary">DronAcademy</span>
          <p className="font-display text-3xl font-bold mt-3 max-w-sm leading-tight">
            Your flight path to becoming a certified drone pilot.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2.5 mb-10">
            <div className="h-9 w-9 rounded-sm bg-primary flex items-center justify-center glow-cyan">
              <Plane className="h-5 w-5 text-primary-foreground" strokeWidth={2.5} />
            </div>
            <span className="font-display font-black text-lg">DRON<span className="text-primary">ACADEMY</span></span>
          </Link>

          <h1 className="font-display text-3xl font-bold mb-2">{isLogin ? "Welcome back" : "Create account"}</h1>
          <p className="text-sm text-muted-foreground mb-8">
            {isLogin ? "Log in to resume your training." : "Start your journey to the skies."}
          </p>

          <Button
            type="button"
            variant="outline"
            onClick={loginWithGoogle}
            data-testid="google-signin-btn"
            className="w-full h-11 rounded-sm border-border bg-secondary/40 hover:bg-secondary font-medium mb-5"
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.6 4.1-5.5 4.1-3.3 0-6-2.7-6-6s2.7-6 6-6c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.9 3.5 14.7 2.5 12 2.5 6.8 2.5 2.6 6.7 2.6 12S6.8 21.5 12 21.5c5.6 0 9.3-3.9 9.3-9.4 0-.6-.06-1.1-.15-1.6H12z" />
            </svg>
            Continue with Google
          </Button>

          <div className="flex items-center gap-3 mb-5">
            <span className="h-px flex-1 bg-border" />
            <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">or</span>
            <span className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {!isLogin && (
              <div className="space-y-1.5">
                <Label htmlFor="name">Full name</Label>
                <Input id="name" data-testid="auth-name" value={name} onChange={(e) => setName(e.target.value)}
                  required placeholder="Amelia Earhart" className="bg-secondary/50 border-border h-11" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="auth-email" value={email} onChange={(e) => setEmail(e.target.value)}
                required placeholder="pilot@example.com" className="bg-secondary/50 border-border h-11" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="auth-password" value={password} onChange={(e) => setPassword(e.target.value)}
                required minLength={isLogin ? undefined : 8} placeholder="••••••••" className="bg-secondary/50 border-border h-11" />
              {!isLogin && <p className="text-xs text-muted-foreground">At least 8 characters.</p>}
            </div>

            {error && <p className="text-sm text-destructive" data-testid="auth-error">{error}</p>}

            {!isLogin && (
              <div className="space-y-2.5 pt-1" data-testid="register-acknowledgments">
                {requiredDocs.map((k) => {
                  const d = legalDocs?.docs?.[k];
                  const title = d?.title || k;
                  const linkable = k === "terms" || k === "privacy";
                  const ackText = k === "training" || k === "beta";
                  return (
                    <label key={k} htmlFor={`ack-${k}`} className="flex gap-2.5 items-start cursor-pointer" data-testid={`ack-item-${k}`}>
                      <Checkbox id={`ack-${k}`} checked={!!accepted[k]} data-testid={`ack-check-${k}`}
                        onCheckedChange={(v) => setAccepted((a) => ({ ...a, [k]: !!v }))} className="mt-0.5" />
                      <span className="text-xs text-muted-foreground leading-relaxed">
                        {linkable ? (
                          <>I agree to the{" "}
                            <Link to={`/legal/${k}`} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
                              {title}<ExternalLink className="h-3 w-3" />
                            </Link>.
                          </>
                        ) : ackText ? (d?.content ? d.content.replace(/\*\*/g, "") : title) : title}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}

            <Button type="submit" disabled={loading || (!isLogin && !allAccepted)} data-testid="auth-submit"
              className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-semibold">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isLogin ? "Log in" : "Create Free Account"}
            </Button>
          </form>

          {!isLogin && PRICING_ENABLED && (
            <div className="mt-6 pt-6 border-t border-border" data-testid="enroll-pay-section">
              <p className="text-xs text-muted-foreground mb-3 text-center">
                Prefer to unlock the full course now? You can also upgrade any time after signing up.
              </p>
              <EnrollPayButton className="w-full" variant="secondary" label="Unlock full Ground School — $89" />
              <p className="text-[11px] text-muted-foreground mt-2 text-center">One-time · lifetime access · secure PayPal checkout</p>
            </div>
          )}

          <p className="text-sm text-muted-foreground mt-6 text-center">
            {isLogin ? "New here? " : "Already flying? "}
            <Link to={isLogin ? "/register" : "/login"} className="text-primary hover:underline font-medium" data-testid="auth-switch">
              {isLogin ? "Create an account" : "Log in"}
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
